// Compile resource-limit defaults and helpers for fail-closed/fail-soft modes.
// Example policy: fail-closed in prod, fail-soft in debug.
import type { LuisCompileOptions, LuisCompileWarning } from './types'

export type LuisResourcePolicy = 'fail-closed' | 'fail-soft'

export type LuisResourceLimits = {
  // Hard cap for one @for iteration count (single loop body expansion).
  maxForIterationsPerLoop: number
  // Hard cap for nested loop cartesian product (i * j * k ...).
  maxNestedLoopProduct: number
  // Hard cap for total rendered CSS rule count after expansion.
  maxExpandedRules: number
  // Hard cap for final CSS output character length.
  maxOutputCssChars: number
  // Hard cap for total observed rule definitions collected from source.
  maxObservedRules: number
  // Hard cap for observed rules that can stay active in DOM at once.
  maxObservedActiveRules: number
  // Hard cap for rule-key candidates processed for one mutation token.
  maxObservedRuleKeysPerToken: number
  // Hard cap for DOM mutation tokens handled in one observer batch.
  maxMutationBatchTokens: number
  // Hard cap for observed re-evaluations allowed in one tick/frame.
  maxObservedReevalPerTick: number
  // Hard cap for nested compile-time/runtime import recursion depth.
  maxImportDepth: number
  // Hard cap for total resolved `.luis` import documents.
  maxImportCount: number
  // Hard cap for total bytes collected from resolved imports.
  maxImportedBytes: number
}

export const DEFAULT_LUIS_RESOURCE_LIMITS: LuisResourceLimits = {
  // Example: prevents compile bombs like @for(i,[1-1000000]).
  maxForIterationsPerLoop: 2000,
  // Example: prevents nested explosion like 1000 * 1000 * 100.
  maxNestedLoopProduct: 100000,
  // Example: keeps expanded rule count bounded for parser/render stability.
  maxExpandedRules: 50000,
  // Example: limits giant CSS payloads that can freeze style application.
  maxOutputCssChars: 3000000,
  // Example: limits observed rule registry size in memory.
  maxObservedRules: 5000,
  // Example: limits simultaneous active observed selectors in runtime.
  maxObservedActiveRules: 1000,
  // Example: limits candidate matching work per mutation event.
  maxObservedRuleKeysPerToken: 300,
  // Example: limits observer queue work processed per callback batch.
  maxMutationBatchTokens: 1500,
  // Example: limits observed re-check loops per frame/tick.
  maxObservedReevalPerTick: 500,
  // Example: prevents pathological deeply nested import chains.
  maxImportDepth: 64,
  // Example: prevents unbounded import fan-out in large graphs.
  maxImportCount: 2000,
  // Example: prevents memory pressure from huge imported source graphs.
  maxImportedBytes: 4000000,
}

// Resolve effective resource policy from compile options.
export function resolveResourcePolicy(options: LuisCompileOptions): LuisResourcePolicy {
  // Use explicit policy when caller overrides default behavior.
  if (options.resourcePolicy) {
    return options.resourcePolicy
  } else if (options.debug) {
    // Default debug sessions to fail-soft for faster iteration.
    return 'fail-soft'
  } else {
    // Keep production/default behavior fail-closed.
    return 'fail-closed'
  }
}

// Resolve effective resource limits by merging option overrides.
export function resolveResourceLimits(options: LuisCompileOptions): LuisResourceLimits {
  // Return merged resource-limit object with fallback defaults.
  return {
    ...DEFAULT_LUIS_RESOURCE_LIMITS,
    ...(options.resourceLimits ?? {}),
  }
}

// Build standardized warning payload for soft resource-limit violations.
export function buildResourceLimitWarning(
  line: number,
  message: string,
): LuisCompileWarning {
  // Return warning payload used by fail-soft limit handling.
  return {
    code: 'resource-limit',
    line,
    message,
  }
}

// Throw one standardized error for fail-closed resource-limit violations.
export function throwResourceLimitError(message: string): never {
  // Throw hard error so compile pipeline stops immediately.
  throw new Error(`[LentyStyle][resource-limit] ${message}`)
}
